import React from "react";
import {
  TournamentResultsHeader,
  TournamentResultsFilters,
  TournamentResultsContent,
  ResultsCacheUpdateBanner,
} from "./components";

// Custom hooks
import {
  useTournamentResults,
  useTournamentExports,
  usePayoutManagement,
} from "./hooks";
import {
  shouldShowResultPrizes,
  stripResultPrizes,
} from "../../utils/prizeSnapshot";

export const TournamentResults = ({
  results = [],
  payouts = null,
  showPayouts: _serverShowPayouts = false,
  isPreviewingPayouts = false,
  setShowPayouts,
  userPermissions = { canManage: false, userRole: null },
  tournament = null,
  organizationName = null,
  tournamentId,
  tournamentSlug,
  refetch,
  verificationStatus = null,
  refetchVerification = null,
  isUpdating = false,
  updateError = null,
}) => {
  // Use server-provided permissions and tournament data
  const { canManage } = userPermissions;
  const tournamentName = tournament?.name;

  // Custom hooks for different concerns
  const {
    isPayoutFinalized,
    isResultsFinalized,
    eventPagination,
    searchTerm,
    selectedEvent,
    filteredResults,
    eventNames,
    setSearchTerm,
    setSelectedEvent,
    handlePageChange,
    handleItemsPerPageChange,
    getPaginatedEventData,
  } = useTournamentResults(results, verificationStatus);

  const { generatePDF, generateCSV, isGeneratingPDF } = useTournamentExports(
    results,
    tournamentName,
    tournamentSlug || tournamentId,
    organizationName,
    isResultsFinalized
  );

  const { handleVerifyPayouts, handleUnlockPayouts } = usePayoutManagement(
    tournamentId,
    userPermissions,
    refetch,
    refetchVerification
  );

  const handleLockResults = async () => {
    const locked = await handleVerifyPayouts();
    if (locked && setShowPayouts) {
      setShowPayouts(false);
    }
  };

  const handleCancelPreview = () => {
    if (setShowPayouts) {
      setShowPayouts(false);
    }
  };

  const handleUnlockResults = async () => {
    await handleUnlockPayouts();
    if (setShowPayouts) {
      setShowPayouts(false);
    }
  };

  const showPrizeAmounts = shouldShowResultPrizes({
    isResultsFinalized,
    isPreviewingPayouts,
  });
  const resultsForDisplay = showPrizeAmounts
    ? filteredResults
    : stripResultPrizes(filteredResults);

  // Handle CSV generation with current filtered results
  const handleGenerateCSV = () => {
    generateCSV(resultsForDisplay, showPrizeAmounts);
  };

  return (
    <div className="w-full max-w-full overflow-x-hidden">
      <div className="card-board overflow-hidden mb-6">
        <div className="bg-brand-charcoal text-brand-cream px-6 py-4">
          <h1 className="font-display font-bold text-xl text-brand-cream tracking-tight">
            Board
          </h1>
          <p className="text-sm text-brand-mist mt-1">
            Places from posted scores.
          </p>
        </div>
        <div
          className={`flex flex-col lg:flex-row gap-4 items-start lg:items-center p-6 ${
            isUpdating || updateError ? "justify-between" : "lg:justify-end"
          }`}
        >
          <ResultsCacheUpdateBanner
            isUpdating={isUpdating}
            updateError={updateError}
            onRetry={refetch}
            updatingLabel="Refreshing places after new scores"
          />
          <TournamentResultsHeader
            tournamentName={tournamentName}
            canManage={canManage}
            isPayoutFinalized={isPayoutFinalized}
            isResultsFinalized={isResultsFinalized}
            isPreviewingPayouts={isPreviewingPayouts}
            onVerifyPayouts={handleLockResults}
            onUnlockPayouts={handleUnlockResults}
            onCancelPreview={handleCancelPreview}
            onSetShowPayouts={setShowPayouts}
            onGeneratePDF={generatePDF}
            onGenerateCSV={handleGenerateCSV}
            isGeneratingPDF={isGeneratingPDF}
            loading={false}
          />
        </div>

        <div className="px-6 pb-6">
          <TournamentResultsFilters
            searchTerm={searchTerm}
            setSearchTerm={setSearchTerm}
            selectedEvent={selectedEvent}
            setSelectedEvent={setSelectedEvent}
            eventNames={eventNames}
            filteredResultsCount={filteredResults.length}
            totalResultsCount={results.length}
          />
        </div>
      </div>

      <TournamentResultsContent
        filteredResults={resultsForDisplay}
        allResults={results}
        getPaginatedEventData={getPaginatedEventData}
        eventPagination={eventPagination}
        serverShowPayouts={showPrizeAmounts}
        tournamentId={tournamentId}
        handlePageChange={handlePageChange}
        handleItemsPerPageChange={handleItemsPerPageChange}
      />
    </div>
  );
};
